import {
  AttributeLink,
  AttributeSetup,
  DATA_TRANSCLUDE,
  Engine,
  getTarget,
  link,
} from '@acng/frontend-stargazer';
import {set as setAttr, get as getAttr, has as hasAttr} from '@acng/frontend-bounty/dom/attribute.js';
import {throttle} from '@acng/frontend-bounty/timing/throttle.js';
import {after, append, remove as removeElement} from '@acng/frontend-bounty/dom/move.js';
import {push, size, slice} from '@acng/frontend-bounty/std/array.js';
import {create, DIV} from '@acng/frontend-bounty/dom/element.js';
import {createMap, each, get, remove, set} from '@acng/frontend-bounty/collection.js';

import {all as waitForAll} from '@acng/frontend-bounty/std/control.js';
import {undefined} from '@acng/frontend-bounty/std/value.js';
import {parseInteger} from '@acng/frontend-bounty/std/number.js';
import {guard, IS_ELEMENT, IS_TEMPLATE} from '@acng/frontend-rubicon';
import {Watch} from '@acng/frontend-relativity';
import {attributeSelector} from '@acng/frontend-bounty/dom/query.js';
import {ctxList} from '../context/list';
import {LIST_LIMIT} from './list-limit';
import {LIST_OFFSET} from './list-offset.js';
import {LIST_REVERSE} from './list-reverse.js';
import {animate} from '../service/animate-class';

/**
 * Repeat the template for each list item of the {@link ctxList | provided list}.
 *
 * @group DOM Attribute
 */
const name = 'list-repeat';

AttributeSetup(name, () => {
  const watch = Watch(attributeSelector(name), ctxList);

  AttributeLink(name, (element) => () => {
    /**
     * @type {Map<unknown, Engine>}
     */
    let items = createMap();

    const removeAllPreviousItems = () =>
      each(items, (engine) => {
        if (engine) {
          const target = getTarget(engine);
          ASSERT: guard(target, IS_ELEMENT);
          animate(target, 'ng-leave').then(() => {
            removeElement(target);
            engine.disconnect();
          });
        }
      });

    const render = throttle(
      /**
       * @param {Element} element
       * @param {NovaList<unknown>} list
       */
      async (element, list) => {
        let previous = element;

        /**
         * @type {Promise<void>[]}
         */
        let tasks = [];

        /**
         * @type {Map<unknown, Engine>}
         */
        const nextItems = createMap();
        ASSERT: guard(element, IS_TEMPLATE, `${name} element`);
        let {initial, more} = list;

        const offset = parseInteger(getAttr(element, LIST_OFFSET)) || 0;
        const limit = parseInteger(getAttr(element, LIST_LIMIT));
        const reverse = hasAttr(element, LIST_REVERSE);

        for (let l = size(initial); l < push(initial, ...(await more(size(initial)))); l = size(initial));

        if (limit || offset) {
          initial = slice(initial, offset, limit ? offset + limit : undefined);
        }

        if (reverse) {
          initial = initial.reverse();
        }

        for (const item of initial) {
          let engine = get(items, item);

          if (engine) {
            // Move existing item element to correct position.
            const target = getTarget(engine);
            ASSERT: guard(target, IS_ELEMENT);
            if (target.previousSibling != previous) {
              // after(previous, target); //TODO: fix for non hot or not lists!!!
            }
            previous = target;
            set(nextItems, item, engine);
            remove(items, item);
          } else {
            // Create item element at correct position.
            const engine = new Engine(element);
            const target = create(DIV);
            after(previous, target);
            previous = target;
            set(nextItems, item, engine);

            push(
              tasks,
              (async () => {
                const linkFn = await list.ItemLink(item);
                append(target, link(engine, {}, target, linkFn));
                engine.connect();
                animate(target, 'ng-enter');
              })().catch((err) => {
                console.error(err);
                target.remove();
                remove(nextItems, item);
              }),
            );
          }
        }

        removeAllPreviousItems();
        items = nextItems;
        await waitForAll(tasks);
      },
    );

    watch(element, render);

    return removeAllPreviousItems;
  });

  return (element) => {
    ASSERT: guard(element, IS_TEMPLATE, `${name} element`);
    setAttr(element, DATA_TRANSCLUDE);
  };
});

export {name as NOVA_REPEAT};
